import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppointmentsRange, getProfessionals, patchAppointment } from '../services/api';
import AppointmentActionsModal from '../components/AppointmentActionsModal';
import './BookingsPage.css';

// --- date helpers (local-time, no UTC drift) ------------------------------
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISO() {
  return toISODate(new Date());
}

function parseISO(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function shiftISO(dateStr, deltaDays) {
  const dt = parseISO(dateStr);
  dt.setDate(dt.getDate() + deltaDays);
  return toISODate(dt);
}

// Monday-based start of the week containing `dateStr`.
function startOfWeek(dateStr) {
  const dt = parseISO(dateStr);
  const dow = (dt.getDay() + 6) % 7; // 0 = Monday
  dt.setDate(dt.getDate() - dow);
  return toISODate(dt);
}

// Period boundaries for the calendar (week = 7 days; month = full weeks grid).
function periodRange(mode, anchor) {
  if (mode === 'week') {
    const start = startOfWeek(anchor);
    return { start, end: shiftISO(start, 6) };
  }
  const a = parseISO(anchor);
  const firstOfMonth = toISODate(new Date(a.getFullYear(), a.getMonth(), 1));
  const lastOfMonth = toISODate(new Date(a.getFullYear(), a.getMonth() + 1, 0));
  const start = startOfWeek(firstOfMonth);
  let end = startOfWeek(lastOfMonth);
  end = shiftISO(end, 6);
  return { start, end };
}

// Build the array of day cells (ISO strings) between start and end inclusive.
function daysBetween(start, end) {
  const out = [];
  let cursor = start;
  while (cursor <= end) {
    out.push(cursor);
    cursor = shiftISO(cursor, 1);
  }
  return out;
}

const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
const TERMINAL = ['cancelled', 'completed'];
const PALETTE_SIZE = 8;

function BookingsPage() {
  const { t, i18n } = useTranslation();

  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [calMode, setCalMode] = useState('week'); // 'week' | 'month'
  const [anchor, setAnchor] = useState(todayISO());

  // list-view explicit date range (defaults to current week)
  const [listStart, setListStart] = useState(() => startOfWeek(todayISO()));
  const [listEnd, setListEnd] = useState(() => shiftISO(startOfWeek(todayISO()), 6));

  // filters (apply to both views)
  const [filterProf, setFilterProf] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');

  const [professionals, setProfessionals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [reschedError, setReschedError] = useState('');
  const dragItem = useRef(null);

  // The fetch range depends on the active view.
  const fetchRange = useMemo(() => {
    if (view === 'list') return { start: listStart, end: listEnd };
    return periodRange(calMode, anchor);
  }, [view, calMode, anchor, listStart, listEnd]);

  const load = useCallback(
    async (start, end) => {
      if (!start || !end || start > end) {
        setAppointments([]);
        setError(start && end ? t('bookings.invalidRange') : '');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const [apptRes, profRes] = await Promise.all([
          getAppointmentsRange(start, end),
          getProfessionals(),
        ]);
        setAppointments(apptRes?.appointments || []);
        setTruncated(Boolean(apptRes?.truncated));
        setProfessionals(profRes?.professionals || []);
      } catch (err) {
        console.error('Error loading bookings:', err);
        setAppointments([]);
        setProfessionals([]);
        setError(t('bookings.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load(fetchRange.start, fetchRange.end);
  }, [fetchRange, load]);

  // Stable professional → palette-index map (and name lookup).
  const profMeta = useMemo(() => {
    const byId = new Map();
    professionals.forEach((p, idx) => {
      const id = p.professionalId || p.id;
      byId.set(id, {
        name: p.name || p.fullName || t('bookings.unnamedProfessional'),
        colorIndex: idx % PALETTE_SIZE,
      });
    });
    return byId;
  }, [professionals, t]);

  const profName = useCallback(
    (id) => profMeta.get(id)?.name || t('bookings.unassigned'),
    [profMeta, t]
  );
  const profColorClass = useCallback(
    (id) => {
      const meta = profMeta.get(id);
      return meta ? `booking--prof-${meta.colorIndex}` : 'booking--prof-unassigned';
    },
    [profMeta]
  );

  // Apply professional / status / client filters (client-side).
  const filtered = useMemo(() => {
    const q = filterClient.trim().toLowerCase();
    return appointments.filter((a) => {
      if (filterProf && a.professionalId !== filterProf) return false;
      if (filterStatus && a.status !== filterStatus) return false;
      if (q) {
        const hay = `${a.clientName || ''} ${a.clientPhone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [appointments, filterProf, filterStatus, filterClient]);

  // Group filtered appointments by ISO date for the calendar grid.
  const byDate = useMemo(() => {
    const map = new Map();
    filtered.forEach((a) => {
      if (!a.date) return;
      if (!map.has(a.date)) map.set(a.date, []);
      map.get(a.date).push(a);
    });
    map.forEach((list) => list.sort((x, y) => (x.time || '').localeCompare(y.time || '')));
    return map;
  }, [filtered]);

  const calendarDays = useMemo(
    () => daysBetween(fetchRange.start, fetchRange.end),
    [fetchRange]
  );

  const weekdayLabels = useMemo(() => {
    // Monday-first labels rendered in the active locale.
    const base = parseISO(startOfWeek(todayISO()));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      return d.toLocaleDateString(i18n.language, { weekday: 'short' });
    });
  }, [i18n.language]);

  const periodLabel = useMemo(() => {
    try {
      if (view === 'list') return null;
      const startD = parseISO(fetchRange.start);
      if (calMode === 'month') {
        const a = parseISO(anchor);
        return a.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
      }
      const endD = parseISO(fetchRange.end);
      const opts = { day: 'numeric', month: 'short' };
      return `${startD.toLocaleDateString(i18n.language, opts)} – ${endD.toLocaleDateString(
        i18n.language,
        { ...opts, year: 'numeric' }
      )}`;
    } catch {
      return fetchRange.start;
    }
  }, [view, calMode, anchor, fetchRange, i18n.language]);

  const shiftPeriod = (dir) => {
    const step = calMode === 'week' ? 7 : 0;
    if (calMode === 'week') {
      setAnchor((a) => shiftISO(a, dir * step));
    } else {
      const a = parseISO(anchor);
      setAnchor(toISODate(new Date(a.getFullYear(), a.getMonth() + dir, 1)));
    }
  };

  // --- drag to reschedule (calendar only) ---------------------------------
  const onDragStart = (appt) => {
    if (TERMINAL.includes(appt.status)) return;
    dragItem.current = appt;
  };

  const onDropDay = async (targetDate) => {
    const appt = dragItem.current;
    dragItem.current = null;
    if (!appt || appt.date === targetDate || !appt.time) return;
    setReschedError('');
    try {
      // Reagendar mantiene hora + profesional, sólo cambia el día. Ojo (NER-1478):
      // el PATCH crea un appointmentId nuevo, así que recargamos el rango en vez
      // de mutar optimistamente con el id viejo.
      await patchAppointment(appt.appointmentId, {
        startTime: `${targetDate}T${appt.time}`,
        professionalId: appt.professionalId,
      });
      await load(fetchRange.start, fetchRange.end);
    } catch (err) {
      console.error('Error rescheduling (drag):', err);
      setReschedError(t('bookings.rescheduleError'));
    }
  };

  const formatTime = (a) => a.time || '--:--';
  const clientLabel = (a) => a.clientName || a.clientPhone || t('bookings.unknownClient');

  const renderCard = (a, draggable) => (
    <button
      type="button"
      key={a.appointmentId || `${a.date}-${a.time}-${a.clientPhone}`}
      className={`booking-card ${profColorClass(a.professionalId)} ${
        TERMINAL.includes(a.status) ? 'booking-card--terminal' : ''
      }`}
      draggable={draggable && !TERMINAL.includes(a.status)}
      onDragStart={() => onDragStart(a)}
      onClick={() => setActionTarget(a)}
      title={`${formatTime(a)} · ${clientLabel(a)}`}
    >
      <span className="booking-card-time">{formatTime(a)}</span>
      <span className="booking-card-client">{clientLabel(a)}</span>
      {a.serviceName && <span className="booking-card-service">{a.serviceName}</span>}
    </button>
  );

  return (
    <div className="bookings-page">
      <div className="page-header">
        <h1>{t('bookings.title')}</h1>
      </div>

      <div className="bookings-toolbar">
        <div className="bookings-view-toggle" role="tablist" aria-label={t('bookings.viewLabel')}>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'calendar'}
            className={`btn ${view === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('calendar')}
          >
            <span className="material-icons">calendar_month</span>
            {t('bookings.calendarView')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            className={`btn ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('list')}
          >
            <span className="material-icons">list</span>
            {t('bookings.listView')}
          </button>
        </div>

        {view === 'calendar' ? (
          <div className="bookings-period-nav">
            <div className="bookings-mode-toggle">
              <button
                type="button"
                className={`btn ${calMode === 'week' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setCalMode('week')}
              >
                {t('bookings.week')}
              </button>
              <button
                type="button"
                className={`btn ${calMode === 'month' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setCalMode('month')}
              >
                {t('bookings.month')}
              </button>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => shiftPeriod(-1)}
              aria-label={t('bookings.prevPeriod')}
            >
              <span className="material-icons">chevron_left</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setAnchor(todayISO())}>
              {t('bookings.today')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => shiftPeriod(1)}
              aria-label={t('bookings.nextPeriod')}
            >
              <span className="material-icons">chevron_right</span>
            </button>
            {periodLabel && <span className="bookings-period-label">{periodLabel}</span>}
          </div>
        ) : (
          <div className="bookings-range-pick">
            <label className="bookings-range-field">
              <span>{t('common.from')}</span>
              <input
                type="date"
                value={listStart}
                max={listEnd || undefined}
                onChange={(e) => e.target.value && setListStart(e.target.value)}
              />
            </label>
            <label className="bookings-range-field">
              <span>{t('common.to')}</span>
              <input
                type="date"
                value={listEnd}
                min={listStart || undefined}
                onChange={(e) => e.target.value && setListEnd(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      <div className="bookings-filters">
        <select
          className="bookings-filter"
          value={filterProf}
          onChange={(e) => setFilterProf(e.target.value)}
          aria-label={t('bookings.filterProfessional')}
        >
          <option value="">{t('bookings.allProfessionals')}</option>
          {professionals.map((p) => (
            <option key={p.professionalId || p.id} value={p.professionalId || p.id}>
              {p.name || p.fullName}
            </option>
          ))}
        </select>
        <select
          className="bookings-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label={t('bookings.filterStatus')}
        >
          <option value="">{t('bookings.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`agenda.status.${s}`, s)}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="bookings-filter bookings-filter-search"
          placeholder={t('bookings.searchClient')}
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          aria-label={t('bookings.searchClient')}
        />
        {(filterProf || filterStatus || filterClient) && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setFilterProf('');
              setFilterStatus('');
              setFilterClient('');
            }}
          >
            {t('common.clear')}
          </button>
        )}
      </div>

      {truncated && <p className="bookings-truncated-note">{t('bookings.truncatedNote')}</p>}
      {error && <div className="error-message">{error}</div>}
      {reschedError && <div className="error-message">{reschedError}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : view === 'calendar' ? (
        <div className={`bookings-calendar bookings-calendar--${calMode}`}>
          {calMode === 'month' && (
            <div className="bookings-weekday-row">
              {weekdayLabels.map((w) => (
                <div key={w} className="bookings-weekday">
                  {w}
                </div>
              ))}
            </div>
          )}
          <div className={`bookings-grid bookings-grid--${calMode}`}>
            {calendarDays.map((iso) => {
              const dayAppts = byDate.get(iso) || [];
              const d = parseISO(iso);
              const isToday = iso === todayISO();
              return (
                <div
                  key={iso}
                  className={`bookings-day ${isToday ? 'bookings-day--today' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropDay(iso)}
                >
                  <div className="bookings-day-header">
                    {calMode === 'week' && (
                      <span className="bookings-day-weekday">
                        {d.toLocaleDateString(i18n.language, { weekday: 'short' })}
                      </span>
                    )}
                    <span className="bookings-day-num">{d.getDate()}</span>
                  </div>
                  <div className="bookings-day-items">
                    {dayAppts.length === 0 ? (
                      <span className="bookings-day-empty">·</span>
                    ) : (
                      dayAppts.map((a) => renderCard(a, true))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="bookings-drag-hint">{t('bookings.dragHint')}</p>
        </div>
      ) : (
        <div className="bookings-list">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('bookings.col.date')}</th>
                  <th>{t('bookings.col.time')}</th>
                  <th>{t('bookings.col.client')}</th>
                  <th>{t('bookings.col.service')}</th>
                  <th>{t('bookings.col.professional')}</th>
                  <th>{t('bookings.col.status')}</th>
                  <th aria-label={t('common.actions')} />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      {t('bookings.empty')}
                    </td>
                  </tr>
                ) : (
                  [...filtered]
                    .sort((a, b) =>
                      `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)
                    )
                    .map((a) => (
                      <tr key={a.appointmentId || `${a.date}-${a.time}-${a.clientPhone}`}>
                        <td>{a.date}</td>
                        <td>{formatTime(a)}</td>
                        <td>{clientLabel(a)}</td>
                        <td>{a.serviceName || '—'}</td>
                        <td>
                          <span className={`booking-prof-chip ${profColorClass(a.professionalId)}`}>
                            {profName(a.professionalId)}
                          </span>
                        </td>
                        <td>
                          <span className={`booking-status booking-status--${a.status}`}>
                            {t(`agenda.status.${a.status}`, a.status || '—')}
                          </span>
                        </td>
                        <td className="bookings-row-action">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setActionTarget(a)}
                          >
                            {t('bookings.manage')}
                          </button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {actionTarget && (
        <AppointmentActionsModal
          appointment={actionTarget}
          professionals={professionals}
          onClose={(changed) => {
            setActionTarget(null);
            if (changed) load(fetchRange.start, fetchRange.end);
          }}
        />
      )}
    </div>
  );
}

export default BookingsPage;
